<template>
  <aside
    class="side-nav"
    :style="{ width: typeof width === 'number' ? `${width}px` : width }"
    role="navigation"
    aria-label="Section navigation"
  >
    <!-- Side Nav Header -->
    <div v-if="$slots.header" class="side-nav__header">
      <slot name="header" />
    </div>

    <!-- Side Nav Items -->
    <div class="side-nav__list">
      <template v-for="(item, index) in items">
        <!-- Section header -->
        <div
          v-if="item.header"
          :key="`header-${index}`"
          class="side-nav__header-text"
        >
          {{ item.header }}
        </div>

        <!-- Simple nav item -->
        <side-nav-item
          v-else-if="!item.children || item.children.length === 0"
          :key="`simple-${index}`"
          :item="item"
          :is-active="item.active || false"
          default-icon="Circle"
          @click="handleItemClick(item, $event)"
        />

        <!-- Nav item with children -->
        <div
          v-else
          :key="`group-${index}`"
          class="side-nav__group"
        >
          <button
            class="side-nav__item side-nav__item--parent"
            :class="{ 'side-nav__item--expanded': isItemExpanded(index) }"
            :aria-expanded="isItemExpanded(index) ? 'true' : 'false'"
            :aria-label="`${item.label} (expandable section)`"
            @click="toggleItem(index)"
            @keydown.enter="toggleItem(index)"
            @keydown.space.prevent="toggleItem(index)"
          >
            <lucide-icon :name="item.icon || 'Circle'" :size="18" class="side-nav__icon" aria-hidden="true" />
            <span class="side-nav__label">
              {{ item.label }}
            </span>
            <lucide-icon
              :name="isItemExpanded(index) ? 'ChevronUp' : 'ChevronDown'"
              :size="16"
              class="side-nav__chevron"
              aria-hidden="true"
            />
          </button>

          <div
            v-if="isItemExpanded(index)"
            class="side-nav__children"
            role="group"
            :aria-label="`${item.label} submenu`"
          >
            <side-nav-item
              v-for="(child, childIndex) in item.children"
              :key="childIndex"
              :item="child"
              :is-child="true"
              :is-active="child.active || false"
              default-icon="Circle"
              @click="handleItemClick(child, $event)"
            />
          </div>
        </div>
      </template>
    </div>

    <!-- Side Nav Footer -->
    <div v-if="$slots.footer" class="side-nav__footer">
      <slot name="footer" />
    </div>
  </aside>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import LucideIcon from '../LucideIcon';
import SideNavItem from './SideNavItem.vue';

export interface SideNavChildItem {
  label: string;
  icon?: string;
  active?: boolean;
  to?: string;
  href?: string;
  action?: string;
}

export interface SideNavItem {
  label: string;
  icon?: string;
  active?: boolean;
  to?: string;
  href?: string;
  action?: string;
  badge?: string | number;
  badgeColor?: string;
  expanded?: boolean;
  header?: string; // Section header text
  children?: SideNavChildItem[];
}

export default Vue.extend({
  name: 'SideNav',

  components: {
    LucideIcon,
    SideNavItem
  },

  props: {
    items: {
      type: Array as PropType<SideNavItem[]>,
      required: true,
      validator: (value: SideNavItem[]) => Array.isArray(value)
    },
    width: {
      type: [Number, String] as PropType<number | string>,
      default: 260,
      validator: (value: number | string) => {
        if (typeof value === 'number') {
          return value > 0 && value <= 1000;
        }
        return typeof value === 'string' && value.length > 0;
      }
    }
  },

  data() {
    return {
      expandedItemsSet: new Set<number>()
    };
  },

  created() {
    // Initialize expanded items from props
    this.items.forEach((item, index) => {
      if (item.expanded) {
        this.expandedItemsSet.add(index);
      }
    });
  },

  methods: {
    toggleItem(index: number) {
      const item = this.items[index];
      const wasExpanded = this.expandedItemsSet.has(index);

      if (wasExpanded) {
        this.expandedItemsSet.delete(index);
      } else {
        this.expandedItemsSet.add(index);
      }

      this.$emit('item-toggle', item, index, !wasExpanded);
      this.$forceUpdate();
    },

    isItemExpanded(index: number): boolean {
      return this.expandedItemsSet.has(index);
    },

    handleItemClick(item: SideNavItem | SideNavChildItem, event?: Event) {
      this.$emit('item-click', item, event);
    }
  }
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/spacing.scss';

.side-nav {
  flex-shrink: 0;
  width: 260px;
  padding-top: $spacing-xl;

  &__header {
    padding: $spacing-lg $spacing-md $spacing-md;
  }

  &__list {
    flex: 1;
    padding: $spacing-xs $spacing-lg;
  }

  &__header-text {
    padding: $spacing-md $spacing-xs $spacing-sm;
    font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #637083;
    color: var(--muted-foreground, #637083);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    line-height: 1;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    padding: 6px $spacing-sm;
    margin-bottom: $spacing-xs;
    font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    font-weight: 400;
    letter-spacing: -0.006em;
    color: #637083;
    color: var(--muted-foreground, #637083);
    text-decoration: none;
    text-rendering: optimizeLegibility;
    border-radius: 0.375rem; /* 6px - rounded-md */
    cursor: pointer;
    transition: all 150ms;
    position: relative;
    border: none;
    background: none;
    width: 100%;
    text-align: left;

    &:hover {
      background-color: #f3f4f6;
      background-color: var(--muted, #f3f4f6) !important;
    }

    &:focus-visible {
      outline: 2px solid #406A94;
      outline: 2px solid var(--primary, #406A94);
      outline-offset: 2px;
    }

    &--parent {
      font-weight: 500;
    }
  }

  &__icon {
    flex-shrink: 0;
    color: inherit;
    opacity: 0.7;
  }

  &__label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chevron {
    margin-left: auto;
    color: hsl(215, 14%, 55%);
    opacity: 0.6;
  }

  &__group {
    margin-bottom: 1px;
  }

  &__children {
    margin-top: 1px;
  }

  &__footer {
    padding: $spacing-md;
    border-top: 1px solid #e5e7eb;
    border-top-color: var(--border, #e5e7eb);
  }
}
</style>

<!-- Dark mode styles (must be unscoped to work with Vue 2) -->
<style lang="scss">
.dark .side-nav {
  &__item {
    // Subtle hover - just a hint of lightening
    &:hover {
      background-color: rgba(255, 255, 255, 0.04) !important;
    }
  }
}
</style>
